import { DialogContent, Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatToUTC } from "@/lib/dateUtils";
import { showApiError } from "@/lib/toast";
import { uploadContents } from "@/sideEffects/api";
import { selectActiveFirm, selectSelectedFolderGuid } from "@/store/appSelectors";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { DatePicker } from "@/components/DatePicker";
import { ScreenSizeSelect } from "../screenSize/ScreenSizeSelect";
import { format } from "date-fns";
import settings from "@/settings.json";

interface Props {
  onSuccess: () => void;
  onOpenChange: (open: boolean) => void;
}

interface UnsplashImage {
  id: string;
  urls: {
    raw: string;
    full: string;
    regular: string;
    small: string;
    thumb: string;
  };
  alt_description: string;
  description: string;
  user: {
    name: string;
    username: string;
  };
  width: number;
  height: number;
}

// Form schema for the image properties
const imagePropsSchema = z.object({
  name: z.string().min(1),
  startDate: z.string().min(1),
  endDate: z.string().min(1),
  screenSizeId: z.number().min(1),
  duration: z.number().min(1),
});

type ImagePropsFormValues = z.infer<typeof imagePropsSchema>;

// Add files to FormSchemaType for the mutation
interface ExtendedFormValues extends ImagePropsFormValues {
  files: File[];
}

const UNSPLASH_ACCESS_KEY = settings.UnsplashAccessKey;

export const CreateFromStockGallery: React.FC<Props> = ({ onSuccess, onOpenChange }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const selectedFolderGuid = useSelector(selectSelectedFolderGuid);

  const [searchQuery, setSearchQuery] = useState("");
  const [images, setImages] = useState<UnsplashImage[]>([]);
  const [selectedImage, setSelectedImage] = useState<UnsplashImage | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [isImagePropsOpen, setIsImagePropsOpen] = useState(false);
  const [searchMode, setSearchMode] = useState<"trending" | "search">("trending");

  // Form for image properties
  const form = useForm<ImagePropsFormValues>({
    resolver: zodResolver(imagePropsSchema),
    defaultValues: {
      name: "",
      startDate: format(new Date(), "dd-MM-yyyy HH:mm"),
      endDate: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), "dd-MM-yyyy HH:mm"),
      screenSizeId: 0,
      duration: 10,
    },
  });

  // Function to search for images from Unsplash
  const searchImages = useCallback(async (query: string, pageNum = 1) => {
    if (!query.trim()) {
      // Reset to trending images when search query is empty
      setPage(1);
      setSearchMode("trending");
      return;
    }

    setIsSearching(true);
    setSearchMode("search");
    try {
      const response = await fetch(
        `https://api.unsplash.com/search/photos?query=${encodeURIComponent(
          query
        )}&page=${pageNum}&per_page=24&client_id=${UNSPLASH_ACCESS_KEY}`
      );

      const data = await response.json();
      if (pageNum === 1) {
        setImages(data.results);
      } else {
        setImages((prevImages) => [...prevImages, ...data.results]);
      }
    } catch (error) {
      console.error("Error fetching images from Unsplash:", error);
      if (error instanceof Error) {
        showApiError(error);
      }
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Function to handle image selection
  const handleImageSelect = (image: UnsplashImage) => {
    setSelectedImage(image);
    setIsImagePropsOpen(true);
    // Set the name field to the image description or alt_description
    form.setValue("name", image.description || image.alt_description || "Unsplash Image");
  };

  // Function to download image and prepare for upload
  const downloadAndPrepareImage = async (image: UnsplashImage, formData: ImagePropsFormValues): Promise<void> => {
    try {
      // Download the image
      const response = await fetch(image.urls.full);
      const blob = await response.blob();

      // Create a File object from the blob
      const imageFile = new File([blob], `${formData.name}.png`, { type: "image/png" });

      // Trigger the upload mutation
      uploadMutation.mutate({
        ...formData,
        files: [imageFile],
      } as ExtendedFormValues);
    } catch (error) {
      console.error("Error downloading image:", error);
      if (error instanceof Error) {
        showApiError(error);
      }
    }
  };

  const uploadMutation = useMutation({
    mutationFn: (data: ExtendedFormValues) => {
      const fileExtension = "png";
      return uploadContents({
        firmGuid: String(activeFirm?.firmGuid),
        isCustom: false,
        files: data.files,
        startDate: formatToUTC(data.startDate),
        endDate: formatToUTC(data.endDate),
        folderGuid: selectedFolderGuid,
        name: `${data.name}.${fileExtension}`,
        screenSizeId: data.screenSizeId,
        duration: data.duration,
      });
    },
    onSuccess: () => {
      onOpenChange(false);
      onSuccess();
    },
    onError: showApiError,
  });

  // Handle search form submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    searchImages(searchQuery, 1);
  };

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);

    // If the input is cleared, reset to trending images
    if (!newValue.trim()) {
      setPage(1);
      fetchTrendingImages();
    }
  };

  // Handle clear search button click
  const handleClearSearch = () => {
    setSearchQuery("");
    setPage(1);
    fetchTrendingImages();
  };

  // Handle load more button click
  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);

    if (searchMode === "search") {
      // Load more search results
      searchImages(searchQuery, nextPage);
    } else {
      // Load more trending images
      fetchTrendingImages(nextPage);
    }
  };

  // Handle image props form submission
  const onSubmit = (values: ImagePropsFormValues) => {
    if (selectedImage) {
      downloadAndPrepareImage(selectedImage, values);
    }
  };

  // Function to fetch trending images
  const fetchTrendingImages = async (pageNum = 1) => {
    setIsSearching(true);
    setSearchMode("trending");
    try {
      const response = await fetch(
        `https://api.unsplash.com/photos?page=${pageNum}&per_page=24&order_by=popular&client_id=${UNSPLASH_ACCESS_KEY}`
      );
      const data = await response.json();

      if (pageNum === 1) {
        setImages(data);
      } else {
        setImages((prevImages) => [...prevImages, ...data]);
      }
    } catch (error) {
      console.error("Error fetching trending images:", error);
      if (error instanceof Error) {
        showApiError(error);
      }
    } finally {
      setIsSearching(false);
    }
  };

  // Initialize with trending images
  useEffect(() => {
    fetchTrendingImages();
  }, []);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[80vw] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("content.stockGallery")}</DialogTitle>
          <DialogDescription>{t("content.searchAndSelectImage")}</DialogDescription>
        </DialogHeader>

        {/* Search bar */}
        {!isImagePropsOpen && (
          <form onSubmit={handleSearch} className="flex items-center space-x-2 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                type="text"
                placeholder={t("content.searchPlaceholder")}
                value={searchQuery}
                onChange={handleSearchInputChange}
                className="pl-9 pr-4"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-2.5 top-2.5"
                  aria-label={t("content.clear")}
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              )}
            </div>
            <Button type="submit" disabled={isSearching}>
              {isSearching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
              {t("content.search")}
            </Button>
          </form>
        )}

        {/* Image grid */}
        {!isImagePropsOpen ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image) => (
              <div
                key={image.id}
                className="relative group rounded-md overflow-hidden cursor-pointer border hover:border-primary transition-colors"
                onClick={() => handleImageSelect(image)}
              >
                <div className="relative pb-[75%]">
                  <img
                    src={image.urls.small}
                    alt={image.alt_description || "Unsplash image"}
                    className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-end">
                  <div className="p-2 w-full text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-sm font-medium truncate">{image.alt_description || "Untitled"}</p>
                    <p className="text-xs truncate">By {image.user.name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Image properties form */
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="w-1/3">
                <div className="rounded-md overflow-hidden">
                  {selectedImage && (
                    <img
                      src={selectedImage.urls.small}
                      alt={selectedImage.alt_description || "Selected image"}
                      className="w-full h-auto object-contain"
                    />
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {selectedImage?.user.name ? `Photo by ${selectedImage.user.name}` : ""}
                </p>
              </div>

              <div className="w-2/3">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("common.name")}</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="duration"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("contentGroup.duration")}</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              value={field.value}
                              onChange={(e) => field.onChange(Number(e.target.value))}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="screenSizeId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("entities.screenSize")}</FormLabel>
                          <FormControl>
                            <ScreenSizeSelect field={field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="startDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("common.startDate")}</FormLabel>
                            <FormControl>
                              <DatePicker value={field.value} onChange={field.onChange} withTime />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="endDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("common.endDate")}</FormLabel>
                            <FormControl>
                              <DatePicker value={field.value} onChange={field.onChange} withTime />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex justify-between pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsImagePropsOpen(false)}>
                        {t("common.back")}
                      </Button>
                      <Button type="submit" disabled={uploadMutation.isPending}>
                        {uploadMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {t("content.upload")}
                      </Button>
                    </div>
                  </form>
                </Form>
              </div>
            </div>
          </div>
        )}

        {/* Load more button */}
        {!isImagePropsOpen && images.length > 0 && (
          <div className="mt-6 flex justify-center">
            <Button variant="outline" onClick={handleLoadMore} disabled={isSearching} className="w-1/3">
              {isSearching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("content.loadMore")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
